import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import './Navigation.css';

const Navigation: React.FC = () => {
  const location = useLocation();
  const { currentUser, signOut, isAuthenticated, loading } = useAuth();

  type NavItem = { path: string; label: string; icon: string };

  const topItems: NavItem[] = [
    { path: '/', label: 'Dashboard', icon: '📊' },
  ];

  const navSections: { title: string; items: NavItem[] }[] = [
    {
      title: 'Profile',
      items: [
        { path: '/profile', label: 'Profile', icon: '👤' },
        { path: '/health', label: 'Health', icon: '❤️' },
        { path: '/preferences', label: 'Preferences', icon: '⚙️' },
      ],
    },
    {
      title: 'Goals',
      items: [
        { path: '/goals', label: 'Goals', icon: '🎯' },
        { path: '/projects', label: 'Projects', icon: '💼' },
        { path: '/habits', label: 'Habits', icon: '✅' },
      ],
    },
    {
      title: 'Social & Personal',
      items: [
        { path: '/relationships', label: 'Relationships', icon: '👥' },
        { path: '/contacts', label: 'Contacts', icon: '📞' },
        { path: '/bookmarks', label: 'Bookmarks', icon: '🔖' },
        { path: '/blogs', label: 'Blogs', icon: '📝' },
        { path: '/campaigns', label: 'Campaigns', icon: '🎲' },
      ],
    },
    {
      title: 'Assets',
      items: [
        { path: '/vehicles', label: 'Vehicles', icon: '🚗' },
        { path: '/places', label: 'Places', icon: '📍' },
      ],
    },
  ];

  const bottomItems: NavItem[] = [
    { path: '/users', label: 'Users', icon: '🔧' },
  ];

  const isActive = (path: string) =>
    location.pathname === path || (path !== '/' && location.pathname.startsWith(path + '/'));

  const renderItem = (item: NavItem, className = 'nav-item') => (
    <li key={item.path} className={className}>
      <Link
        to={item.path}
        className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
      >
        <span className="nav-icon">{item.icon}</span>
        <span className="nav-label">{item.label}</span>
      </Link>
    </li>
  );

  const adminItems = [
    { path: '/admin', label: 'Admin Panel', icon: '🛡️' },
    { path: '/admin/site-preferences', label: 'Site Admin', icon: '🛠️' },
  ];

  // Don't show navigation on auth pages or public profiles for unauthenticated users
  const isAuthPage = ['/signin', '/signup', '/auth/callback'].includes(location.pathname);
  const isUsernameRoute = location.pathname.match(/^\/[a-zA-Z0-9_-]+$/);
  
  if (isAuthPage) {
    return null;
  }
  
  // For username routes, only show navigation if the user is authenticated and viewing their own profile
  if (isUsernameRoute && !isAuthenticated()) {
    return null;
  }

  // Show loading state while auth is being determined
  if (loading) {
    return (
      <nav className="navigation">
        <div className="nav-header">
          <h1>SelfServe</h1>
        </div>
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          Loading...
        </div>
      </nav>
    );
  }

  return (
    <nav className="navigation">
      <div className="nav-header">
        <h1>SelfServe</h1>
        {isAuthenticated() && currentUser && (
          <div className="user-info">
            <span className="welcome-text">Welcome, {currentUser.firstName}!</span>
            <span className="user-role">({currentUser.role})</span>
          </div>
        )}
      </div>

      {isAuthenticated() ? (
        <>
          <ul className="nav-list">
            {topItems.map((item) => renderItem(item))}
            {navSections.map((section) => (
              <li key={section.title} className="nav-section">
                <div className="nav-section-title">{section.title}</div>
                <ul className="nav-section-list">
                  {section.items.map((item) => renderItem(item))}
                </ul>
              </li>
            ))}
            {bottomItems.map((item) => renderItem(item))}
            {currentUser?.role === 'admin' && adminItems.map((item) => renderItem(item, 'nav-item admin-item'))}
          </ul>
          
          <div className="nav-auth">
            <button onClick={signOut} className="sign-out-btn">
              <span className="nav-icon">🚪</span>
              <span className="nav-label">Sign Out</span>
            </button>
          </div>
        </>
      ) : (
        <div className="nav-auth-guest">
          <Link to="/signin" className="auth-nav-link">
            <span className="nav-icon">🔑</span>
            <span className="nav-label">Sign In</span>
          </Link>
          <Link to="/signup" className="auth-nav-link">
            <span className="nav-icon">📝</span>
            <span className="nav-label">Sign Up</span>
          </Link>
        </div>
      )}
    </nav>
  );
};

export default Navigation;